/**
 * Date / year helpers used across the SPA.
 *
 * Intentionally small.  Not every ``new Date().getFullYear()`` call
 * needs a wrapper — the goal is to consolidate the cases where the
 * SAME pattern is reimplemented across multiple files, so a
 * regression in the format (or the cutoff date for the tax year)
 * doesn't have to be fixed in five places.
 *
 * Three patterns surfaced as duplicated in the May 2026 refactor
 * pass:
 *
 * 1. ``formatLongDate(date)`` — "June 12, 2024".  Four callers in
 *    the refund / cancel pages, all with the exact same locale +
 *    options.  This is the highest-leverage extraction.
 *
 * 2. ``yearBuiltMaxPlausible()`` — the upper bound on a plausible
 *    ``year_built`` for property-detail forms.  Properties under
 *    construction can legitimately report a year-built of next
 *    calendar year (assessed by the CAD before the slab pours).
 *    Two callers in the verify-address flow.
 *
 * 3. ``currentCalendarYear()`` — wraps ``new Date().getFullYear()``.
 *    The one-liner is fine in place; the wrapper exists so test
 *    code can monkeypatch a deterministic year and so the call
 *    site reads as "the year used for X" rather than a bare
 *    expression.  Use the wrapper for *new* code; leave existing
 *    one-liners alone unless they're touched for other reasons.
 */

/**
 * Format an ISO string or Date as a long human-readable date —
 * "June 12, 2024".  Returns an empty string for null/undefined
 * input so callers can safely interpolate the result without
 * branching.
 *
 * Locale is fixed to en-US because the SPA's audience is Texas
 * homeowners and the dates we show are local government records
 * (appraisal-roll dates, sale dates, payment timestamps).  No
 * i18n requirement — having a consistent format across the
 * product matters more.
 */
export function formatLongDate(value) {
  if (value == null) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/**
 * Upper bound for the ``year_built`` input on property-detail
 * forms.  Returns the current calendar year + 1, so under-
 * construction properties whose CAD records list next year's
 * completion are accepted by the validator.
 *
 * Used by:
 *   - containers/FreeAssessmentPage/VerifyAddress.js validation
 *   - components/VerifyAddressForm/index.js native input max=
 */
export function yearBuiltMaxPlausible() {
  return new Date().getFullYear() + 1;
}

/**
 * Wrapper for ``new Date().getFullYear()`` — same value, named.
 * Exists so a test can monkeypatch it and so call sites read
 * intentfully ("the year we use for the copyright" vs. "the year
 * for the tax-roll fallback").  Cheap to use; cheap to leave.
 */
export function currentCalendarYear() {
  return new Date().getFullYear();
}
